import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { DateRangeQuickOption } from '@workspace/ui/components/DatePicker'

export function useDateRangeQuickOptions(): DateRangeQuickOption[] {
    const { t } = useTranslation()

    return useMemo(() => {
        const today = dayjs()
        const format = (date: dayjs.Dayjs) => date.format('YYYY-MM-DD')

        // Calculate this week start from Monday (day 1) instead of Sunday (day 0)
        const dayOfWeek = today.day() // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
        const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1 // Convert Sunday (0) to 6 days from Monday
        const thisWeekStart = today.subtract(daysFromMonday, 'day').startOf('day')
        const thisWeekEnd = thisWeekStart.add(6, 'day') // Monday to Sunday

        return [
            { key: 'today', label: t('logs.quickOptions.today'), value: { start: format(today), end: format(today) } },
            {
                key: 'yesterday',
                label: t('logs.quickOptions.yesterday'),
                value: { start: format(today.subtract(1, 'day')), end: format(today.subtract(1, 'day')) },
            },
            {
                key: 'thisWeek',
                label: t('logs.quickOptions.thisWeek'),
                value: { start: format(thisWeekStart), end: format(thisWeekEnd) },
            },
            {
                key: 'last7',
                label: t('logs.quickOptions.last7'),
                value: { start: format(today.subtract(6, 'day')), end: format(today) },
            },
            {
                key: 'last28',
                label: t('logs.quickOptions.last28'),
                value: { start: format(today.subtract(27, 'day')), end: format(today) },
            },
            {
                key: 'thisMonth',
                label: t('logs.quickOptions.thisMonth'),
                value: { start: format(today.startOf('month')), end: format(today) },
            },
            {
                key: 'lastMonth',
                label: t('logs.quickOptions.lastMonth'),
                value: {
                    start: format(today.subtract(1, 'month').startOf('month')),
                    end: format(today.subtract(1, 'month').endOf('month')),
                },
            },
            {
                key: 'thisYear',
                label: t('logs.quickOptions.thisYear'),
                value: { start: format(today.startOf('year')), end: format(today) },
            },
            {
                key: 'allTime',
                label: t('logs.quickOptions.allTime'),
                value: { start: null, end: null },
            },
        ]
    }, [t])
}
